// Nurse2PatientQueue.tsx

import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "../../../api/axiosConfig";
import "../../../center admin css/centerAdmin.css";

/** ====== Center Workflow response types (simplified) ====== */
type WaitingItem = {
  _id: string;
  appointmentId?: string;
  stationId: string; // e.g. "NURSE2"
  sessionId?: string;
  createdAt: string; // ISO
  state?: "WAITING" | "IN_PROGRESS" | "DONE" | string;
  startedAt?: string; // present for inProgress
  child: {
    childId: any;
    name: string;
    age: number;
    gender: "M" | "F" | "Male" | "Female" | string;
    emrId: string;
    profile?: string;
  };
  pathId?: string;
  sessionStartAt?: string; // ISO start time
};

type StationPatientsResp = {
  waiting?: WaitingItem[];
  inProgress?: WaitingItem[];
  completed?: WaitingItem[];
};

/** ====== UI item type (keeps your existing look) ====== */
type QueueItem = {
  id: string;
  emrId: string; // Ensure emrId is required for mapping
  date: string; // dd/MM/yy
  name: string;
  genderShort: "M" | "F";
  ageYears: number;
  avatarUrl?: string;
  time?: string; // hh:mm
  _source?: "IN_QUEUE" | "IN_PROGRESS" | "WAITING" | "NO_CHILD";
  state?: "IN_QUEUE" | "IN_PROGRESS" | "WAITING" | "NO_CHILD";
  isEmpty?: boolean;
  sessionId?: string;
  pathOrder?: number;
  rowIndex?: number;
  slotLabel?: string;
  profile?: string;
};

type Props = {
  /** kept for compatibility with AppLayout footer display only */
  centerId: string;
  /** ISO date "YYYY-MM-DD" (optional, footer only) */
  dateISO?: string;
  /** currently active queue item id */
  activeId?: string;
  /** selection callback */
  // onSelect?: (id: string) => void;
  onSelect?: (item: WaitingItem) => void;
  /** optional polling ms */
  autoRefreshMs?: number;
  /** workflow station id; defaults to "NURSE2" */
  stationId?: string;
};

function formatDmy(iso?: string) {
  if (!iso) return "";
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

function formatHm(iso?: string) {
  if (!iso) return "";
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

function todayISO() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

const Nurse2PatientQueue: React.FC<Props> = ({
  centerId, // footer only (preserved)
  dateISO,
  activeId,
  onSelect,
  autoRefreshMs,
  stationId = "NURSE2",
}) => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [items, setItems] = useState<QueueItem[]>([]);
  const [rawWaiting, setRawWaiting] = useState<WaitingItem[]>([]);
  const [rawInQueue, setRawInQueue] = useState<WaitingItem[]>([]);
  const [rawInProgress, setRawInProgress] = useState<WaitingItem[]>([]);

  // ✅ NEW: Store presigned URLs
  const [avatarMap, setAvatarMap] = useState<Record<string, string>>({});

  const effectiveDate = dateISO || todayISO();

  const getCenterId = () => {
    try {
      const rawConfig = sessionStorage.getItem("configResponse");

      if (!rawConfig) return "";

      const parsedConfig = JSON.parse(rawConfig);

      return parsedConfig?.data?.[0]?.centerId || "";
    } catch (error) {
      console.error("Failed to read configResponse:", error);
      return "";
    }
  };

  /** ── queue loader (sends token from sessionStorage) ── */
  // async function load() {
  const load = useCallback(async () => {
    if (items.length === 0) setLoading(true); // Initial load only
    setErr(null);

    try {
      const token = sessionStorage.getItem("accessToken");
      if (!token) throw new Error("No access token found. Please log in.");

      const headers = { Authorization: `Bearer ${token}` };

      const centerId = getCenterId();

      if (!centerId) {
        throw new Error("Center ID not found");
      }

      const { data } = await api.centerworkflowApi.get(
        `/queues/NURSE2_Q?center_id=${encodeURIComponent(centerId)}&date=${encodeURIComponent(effectiveDate)}`,
        { headers },
      );

      const waiting = Array.isArray(data?.waiting) ? data.waiting : [];

      const inQueue = Array.isArray(data?.inQueue) ? data.inQueue : [];

      const inProgress = Array.isArray(data?.inProgress) ? data.inProgress : [];

      setRawWaiting(waiting);
      setRawInQueue(inQueue);
      setRawInProgress(inProgress);

      const matrixRows = Array.isArray(data?.matrixRows) ? data.matrixRows : [];

      const mapped: QueueItem[] = matrixRows
        .filter(
          (row: any) =>
            row.state === "IN_QUEUE" ||
            row.state === "IN_PROGRESS" ||
            row.state === "WAITING" ||
            row.state === "NO_CHILD",
        )
        .map((row: any) => {
          const item = row.item;

          const ts = item?.startedAt || item?.sessionStartAt || item?.createdAt;

          return {
            id: item?._id || `empty-${row.row}`,

            emrId: row.child?.childId || "",

            profile: row.child?.profile || item?.child?.profile || "",

            date: formatDmy(ts),

            time: formatHm(ts),

            // slotLabel: row.slotLabel || row.slot_label || "",
            slotLabel:
              row?.scheduledTime?.label ||
              row?.item?.scheduledTime?.label ||
              row?.slotLabel ||
              row?.slot_label ||
              "",

            name:
              row.state === "NO_CHILD"
                ? "No Patient"
                : row.child?.name || "Unknown",

            genderShort: String(row.child?.gender || "M")
              .toUpperCase()
              .startsWith("F")
              ? "F"
              : "M",

            ageYears: Number(row.child?.age ?? 0),

            avatarUrl: undefined,

            _source: row.state,

            state: row.state,

            isEmpty: row.state === "NO_CHILD",

            sessionId: row.sessionId,

            pathOrder: row.item?.pathOrder || 0,

            rowIndex: row.row || 0,
          };
        });

      const sortedMapped = [...mapped].sort(
        (a, b) => (a.rowIndex || 0) - (b.rowIndex || 0),
      );

      setItems(sortedMapped);

      // ✅ Auto-select: prefer an in-progress item; otherwise first waiting/any

      const hasActive = activeId && mapped.some((m) => m.id === activeId);

      if (!hasActive) {
        // const firstInQueue = waiting.find((x: any) => x.state === "IN_QUEUE");
        const firstInQueue = inQueue.find((x: any) => x.state === "IN_QUEUE");

        if (firstInQueue) {
          sessionStorage.setItem(
            "selectedPatient_NURSE2_Q",
            JSON.stringify(firstInQueue),
          );

          sessionStorage.setItem("activeNurse2PatientId", firstInQueue._id);

          onSelect?.(firstInQueue);
        }
      }

      // ✅ NEW: Fetch images
      await fetchImages(mapped, headers);
    } catch (e: any) {
      setErr(e?.message || "Failed to fetch station queue");
      // setItems([]); // Don't clear on transient error to avoid flicker
    } finally {
      setLoading(false);
    }
  }, [activeId, effectiveDate, items.length, onSelect]);

  /* ------------------ 🔐 PRESIGNED IMAGE FETCH (FIXED) ------------------ */
  // async function fetchImages(queueItems: QueueItem[], headers: any) {
  //   const currentCenterId = getCenterId();
  //   if (!currentCenterId) return;

  //   try {
  //     const apptRes = await api.appointment_api.get(
  //       `/api/appointments/center/${encodeURIComponent(
  //         currentCenterId,
  //       )}/date?date=${encodeURIComponent(effectiveDate)}`,
  //       { headers },
  //     );

  //     const slots = apptRes.data?.slots || [];

  //     const bookingSlotMap: Record<string, string> = {};

  //     slots.forEach((slot: any) => {
  //       const slotLabel = slot.slot_label;

  //       (slot.bookings || []).forEach((booking: any) => {
  //         if (booking.child_id) {
  //           bookingSlotMap[booking.child_id] = slotLabel;
  //         }
  //       });
  //     });

  //     const bookings = slots.flatMap((slot: any) => slot.bookings || []);

  //     const newMap: Record<string, string> = {};

  //     for (const b of bookings) {
  //       const filePath = b.child_profile;
  //       if (!filePath || !b.child_id) continue;

  //       // Skip if we already have this image
  //       if (avatarMap[b.child_id]) {
  //         newMap[b.child_id] = avatarMap[b.child_id];
  //         continue;
  //       }

  //       try {
  //         // If already presigned, use as is
  //         if (filePath.includes("X-Amz-Signature")) {
  //           newMap[b.child_id] = filePath;
  //           continue;
  //         }

  //         // ✅ FIX: Manual string splitting instead of new URL()
  //         const parts = filePath.split("/");

  //         // Check structure
  //         if (parts.length < 3) continue;

  //         // Extract Module (first two segments)
  //         const module = `${parts[0]}/${parts[1]}`;

  //         // Extract Key (remainder)
  //         const key = parts.slice(2).join("/");

  //         const img = await api.S3config.get("/view-url", {
  //           params: { module, key },
  //           headers,
  //         });

  //         // Robust response check
  //         const signedUrl = img.data?.url || (img as any).url;
  //         if (signedUrl) {
  //           newMap[b.child_id] = signedUrl;
  //         }
  //       } catch (e) {
  //         // ignore failures
  //       }
  //     }

  //     setAvatarMap((prev) => ({ ...prev, ...newMap }));

  //     setItems((prev) =>
  //       [...prev].sort((a, b) => (a.rowIndex || 0) - (b.rowIndex || 0)),
  //     );
  //   } catch (e) {
  //     console.warn("Avatar load failed", e);
  //   }
  // }
  ///* ------------------ ------------------ */

  async function fetchImages(queueItems: QueueItem[], headers: any) {
    try {
      const newMap: Record<string, string> = {};

      for (const item of queueItems) {
        const emrId = item.emrId?.trim();
        const filePath = item.profile;

        if (!emrId || !filePath) continue;

        // Keep existing cached image if available
        if (avatarMap[emrId]) {
          newMap[emrId] = avatarMap[emrId];
          continue;
        }

        // Already a presigned URL
        if (filePath.includes("X-Amz-Signature")) {
          newMap[emrId] = filePath;
          continue;
        }

        const parts = filePath.split("/");

        if (parts.length < 3) {
          console.warn("Invalid profile path:", filePath);
          continue;
        }

        // Example:
        // checkin/capture-photo/P-2026-000113-0/...
        const module = `${parts[0]}/${parts[1]}`;
        const key = parts.slice(2).join("/");

        try {
          const img = await api.S3config.get("/view-url", {
            params: {
              module,
              key,
            },
            headers,
          });

          const signedUrl = img.data?.url || (img as any).url;

          if (signedUrl) {
            newMap[emrId] = signedUrl;
          }
        } catch (error) {
          console.warn(`Failed to load profile for ${emrId}`, error);
        }
      }

      setAvatarMap((prev) => ({
        ...prev,
        ...newMap,
      }));
    } catch (error) {
      console.warn("Avatar load failed", error);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, stationId]);

  useEffect(() => {
    const handlePatientUpdated = () => {
      load();
    };

    window.addEventListener("patientUpdated", handlePatientUpdated);

    return () => {
      window.removeEventListener("patientUpdated", handlePatientUpdated);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  useEffect(() => {
    if (!autoRefreshMs) return;
    const t = setInterval(load, autoRefreshMs);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, stationId, autoRefreshMs]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    // ✅ Merge avatarMap
    const withImages = items.map((i) => ({
      ...i,
      avatarUrl:
        avatarMap[i.emrId] || avatarMap[i.emrId?.trim()] || i.avatarUrl,
    }));

    if (!q) return withImages;
    return withImages.filter((p) => p.name.toLowerCase().includes(q));
  }, [items, query, avatarMap]);

  return (
    <aside className="gc-queue">
      <div className="gc-queue-header">
        <div className="gc-queue-title">
          Patient Queue
          <span className="gc-queue-underline" />
        </div>

        <div className="gc-queue-searchwrap">
          <input
            className="gc-queue-search"
            placeholder="Search patients..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <span className="gc-queue-searchicon" aria-hidden>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.5 14h-.79l-.28-.27a6.471 6.471 0 0 0 1.57-4.23 6.5 6.5 0 1 0-6.5 6.5 6.471 6.471 0 0 0 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5Zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14Z" />
            </svg>
          </span>
        </div>
      </div>

      <div className="gc-queue-list">
        {loading && items.length === 0 && (
          <div className="gc-queue-empty">Loading…</div>
        )}
        {err && items.length === 0 && (
          <div className="gc-queue-empty">Error: {err}</div>
        )}
        {!loading && !err && filtered.length === 0 && (
          <div className="gc-queue-empty">No patients</div>
        )}

        {filtered.map((p) => {
          const active = p.id === activeId;
          return (
            <button
              key={p.id}
              className={`gc-queue-item ${active ? "active" : ""} ${
                p._source === "IN_QUEUE" || p._source === "IN_PROGRESS"
                  ? "in-progress"
                  : p._source === "NO_CHILD"
                    ? "no-child"
                    : "waiting"
              }`}
              onClick={() => {
                if (p._source === "IN_QUEUE" || p._source === "IN_PROGRESS") {
                  const selectedPatient =
                    rawInQueue.find((x) => x._id === p.id) ||
                    rawInProgress.find((x) => x._id === p.id);

                  if (selectedPatient) {
                    sessionStorage.setItem(
                      "selectedPatient_NURSE2_Q",
                      JSON.stringify(selectedPatient),
                    );

                    sessionStorage.setItem(
                      "activeNurse2PatientId",
                      selectedPatient._id,
                    );

                    // window.dispatchEvent(new Event("patientUpdated"));

                    onSelect?.(selectedPatient);
                  }
                }
              }}
              disabled={p._source !== "IN_QUEUE" && p._source !== "IN_PROGRESS"}
              style={{
                cursor:
                  p._source === "IN_QUEUE" || p._source === "IN_PROGRESS"
                    ? "pointer"
                    : "not-allowed",
                opacity:
                  p._source === "IN_QUEUE" || p._source === "IN_PROGRESS"
                    ? 1
                    : 0.6,
              }}
              title={p.time ? `Time: ${p.time}` : undefined}
            >
              <div className="gc-queue-item-body">
                <div className="gc-queue-avatar">
                  {p.avatarUrl ? (
                    <img src={p.avatarUrl} alt={p.name} />
                  ) : (
                    <div className="gc-queue-avatar-fallback" />
                  )}
                </div>

                <div className="gc-queue-item-info">
                  <div className="gc-queue-item-date">
                    {p.slotLabel ? `${p.slotLabel} · ${p.date}` : p.date}
                  </div>
                  <div className="gc-queue-name">
                    {p.isEmpty ? "No Child" : p.name}
                  </div>

                  <div className="gc-queue-sub">
                    {p.isEmpty
                      ? "Waiting for Child"
                      : `${p.genderShort}, ${p.ageYears} ${
                          p.ageYears === 1 ? "Year" : "Years"
                        }`}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="gc-queue-footer">
        <div className="gc-queue-meta">
          <small>
            Station: <b>{stationId}</b> · Date: <b>{effectiveDate}</b>
          </small>
        </div>
      </div>
    </aside>
  );
};

export default Nurse2PatientQueue;
